import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Award, CheckCircle, Leaf, Zap, Recycle, PenTool, Maximize, Hammer, Gem } from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { useSEO } from '../hooks/useSEO';
// Images
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/pPR23bNLSAVudMWhF2ynaG/_DSC8241.webp';
const STORY_IMAGE = 'https://cdn.magicpatterns.com/uploads/3xL1jkjmSyiggyKFQmbzxH/_DSC8261.webp';
const SUSTAINABILITY_IMAGE = 'https://cdn.magicpatterns.com/uploads/iSsLTMw4wKgenTWkbWEAxu/_DSC8170.webp';
export function AboutPage() {
  useSEO({
    title: 'About Robeta — European Heritage, North American Adventure',
    description: 'Discover the Robeta story. Born in Slovenia, built for North America. European luxury camper vans with sustainable practices, quality certifications, and handcrafted precision.'
  });
  return <main className="bg-white min-h-screen" data-id="element-281">
      {/* 1. HERO SECTION */}
      <section className="relative h-[60vh] min-h-[500px] w-full flex flex-col justify-between overflow-hidden" data-id="element-282">
        <div className="absolute inset-0 z-0" data-id="element-283">
          <img src={HERO_IMAGE} alt="Robeta Craftsmanship" className="w-full h-full object-cover" data-id="element-284" />
          <div className="absolute inset-0 bg-black/40" data-id="element-285" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" data-id="element-286" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10 pt-32" data-id="element-287">
          <Breadcrumbs items={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'About Robeta'
        }]} variant="light" data-id="element-288" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10 pb-16 md:pb-24" data-id="element-289">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="max-w-4xl" data-id="element-290">
            <h1 className="text-5xl md:text-7xl font-bold text-white uppercase tracking-editorial leading-[0.9] mb-6" data-id="element-291">
              Discover Robeta.
            </h1>
            <p className="text-xl md:text-2xl font-light text-white/80 max-w-2xl leading-relaxed" data-id="element-292">
              European luxury camper vans, crafted for North American adventure.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. OUR STORY */}
      <section className="py-24 md:py-32 bg-white" data-id="element-293">
        <div className="container mx-auto px-6 md:px-12" data-id="element-294">
          <div className="flex flex-col lg:flex-row gap-16 lg:gap-24" data-id="element-295">
            {/* Left Column: European Roots */}
            <div className="w-full lg:w-1/2" data-id="element-296">
              <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-297">
                Heritage
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-robeta-black uppercase tracking-editorial mb-8" data-id="element-298">
                Born in Slovenia.
                <br data-id="element-299" />
                Raised on Excellence.
              </h2>
              <div className="space-y-6 text-robeta-black/70 font-light leading-relaxed" data-id="element-300">
                <p data-id="element-301">
                  Robeta was founded in Slovenia, the heart of Europe's
                  caravanning industry. Surrounded by a rich tradition of
                  woodworking and precision manufacturing, we set out with a
                  singular vision: to build camper vans that refuse to
                  compromise.
                </p>
                <p data-id="element-302">
                  Our philosophy is rooted in design-driven innovation. We don't
                  just assemble parts; we engineer solutions. From our
                  proprietary Air Furniture Technology™ to our yacht-inspired
                  interiors, every element of a Robeta van is a testament to
                  European craftsmanship and functional elegance.
                </p>
              </div>
            </div>

            {/* Right Column: North American Expansion */}
            <div className="w-full lg:w-1/2" data-id="element-303">
              <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-304">
                Expansion
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-robeta-black uppercase tracking-editorial mb-8" data-id="element-305">
                A New Standard for
                <br data-id="element-306" />
                North America.
              </h2>
              <div className="space-y-6 text-robeta-black/70 font-light leading-relaxed" data-id="element-307">
                <p data-id="element-308">
                  We recognized that North American travelers share our passion
                  for exploration but demand vehicles built for vast landscapes
                  and diverse climates. Robeta Canada represents our commitment
                  to this continent.
                </p>
                <p data-id="element-309">
                  We haven't just imported our vans; we've adapted them. Every
                  Robeta vehicle sold here is fully compliant with North
                  American safety and utility standards, ensuring that your
                  European luxury experience is supported by local reliability
                  and service. We are here for the long haul, ready to support
                  your journey from the Atlantic to the Pacific.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. DESIGN & CRAFTSMANSHIP */}
      <section className="py-24 md:py-32 bg-robeta-mist/10" data-id="element-310">
        <div className="container mx-auto px-6 md:px-12" data-id="element-311">
          <div className="text-center mb-20" data-id="element-312">
            <h2 className="text-3xl md:text-5xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-313">
              Designed with Intention.
              <br data-id="element-314" />
              Built by Hand.
            </h2>
            <div className="w-24 h-px bg-robeta-copper mx-auto" data-id="element-315" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12" data-id="element-316">
            {[{
            icon: PenTool,
            title: 'Architectural Design',
            desc: 'Interiors that feel like modern apartments, not recreational vehicles. Clean lines, hidden hardware, and open sightlines.'
          }, {
            icon: Maximize,
            title: 'Space Engineering',
            desc: 'Every millimeter is utilized. Our layouts maximize living space without feeling cramped, offering storage where you need it.'
          }, {
            icon: Hammer,
            title: 'Handcrafted Precision',
            desc: 'Our "One Van, One Craftsman" philosophy ensures personal accountability and flawless fit and finish in every build.'
          }, {
            icon: Gem,
            title: 'Premium Materials',
            desc: 'We use high-pressure laminates, marine-grade fabrics, and solid surfaces that are built to endure years of travel.'
          }].map((item, i) => <div key={i} className="flex flex-col items-center text-center group" data-id="element-317">
                <div className="w-16 h-16 rounded-full bg-white border border-robeta-black/5 flex items-center justify-center mb-8 group-hover:border-robeta-copper transition-colors duration-300" data-id="element-318">
                  <item.icon className="w-6 h-6 text-robeta-black group-hover:text-robeta-copper transition-colors duration-300" data-id="element-319" />
                </div>
                <h3 className="text-lg font-bold text-robeta-black uppercase tracking-wide mb-4" data-id="element-320">
                  {item.title}
                </h3>
                <p className="text-robeta-black/60 font-light text-sm leading-relaxed" data-id="element-321">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 4. QUALITY & CERTIFICATIONS */}
      <section className="py-24 bg-white border-y border-robeta-black/5" data-id="element-322">
        <div className="container mx-auto px-6 md:px-12" data-id="element-323">
          <div className="flex flex-col lg:flex-row gap-16 items-center" data-id="element-324">
            <div className="w-full lg:w-1/3" data-id="element-325">
              <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-326">
                Trust & Safety
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-327">
                Certified for
                <br data-id="element-328" />
                Your Peace of Mind.
              </h2>
              <p className="text-robeta-black/60 font-light leading-relaxed" data-id="element-329">
                Navigating regulatory standards is complex. We've done the hard
                work so you can travel with confidence. Robeta vehicles meet or
                exceed all required North American safety and manufacturing
                standards.
              </p>
            </div>

            <div className="w-full lg:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-8" data-id="element-330">
              {[{
              icon: ShieldCheck,
              title: 'CMVSS Compliant',
              desc: 'Fully compliant with Canada Motor Vehicle Safety Standards for road safety and crashworthiness.'
            }, {
              icon: CheckCircle,
              title: 'FMVSS Compliant',
              desc: 'Meets all Federal Motor Vehicle Safety Standards for operation in the United States.'
            }, {
              icon: Award,
              title: 'CSA Z240 RV Series',
              desc: 'Certified to CSA Z240 RV Series standards for electrical, plumbing, and gas systems safety.'
            }, {
              icon: ShieldCheck,
              title: 'esafe.org Designation',
              desc: 'Electrical systems inspected and approved by recognized authorities for safety and reliability.'
            }].map((cert, i) => <div key={i} className="flex gap-6 p-6 border border-robeta-black/5 hover:border-robeta-copper/30 transition-colors bg-gray-50/50" data-id="element-331">
                  <cert.icon className="w-8 h-8 text-robeta-copper flex-shrink-0" data-id="element-332" />
                  <div data-id="element-333">
                    <h3 className="text-sm font-bold text-robeta-black uppercase tracking-wide mb-2" data-id="element-334">
                      {cert.title}
                    </h3>
                    <p className="text-xs text-robeta-black/60 font-light leading-relaxed" data-id="element-335">
                      {cert.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 5. SUSTAINABILITY */}
      <section className="py-24 md:py-32 bg-white" data-id="element-336">
        <div className="container mx-auto px-6 md:px-12" data-id="element-337">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center" data-id="element-338">
            <div className="relative aspect-[4/3] overflow-hidden order-2 lg:order-1" data-id="element-339">
              <img src={SUSTAINABILITY_IMAGE} alt="Sustainable Materials" className="absolute inset-0 w-full h-full object-cover" data-id="element-340" />
            </div>
            <div className="order-1 lg:order-2" data-id="element-341">
              <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-342">
                Responsibility
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-robeta-black uppercase tracking-editorial mb-8" data-id="element-343">
                Treading Lightly.
              </h2>
              <div className="space-y-8" data-id="element-344">
                {[{
                icon: Recycle,
                title: 'Responsible Sourcing',
                desc: 'Our Air Furniture Technology™ utilizes recycled PET materials, diverting thousands of plastic bottles from landfills for every vehicle produced.'
              }, {
                icon: Zap,
                title: 'Energy Efficiency',
                desc: 'Advanced insulation and efficient electrical systems reduce energy consumption, allowing for longer off-grid stays with less impact.'
              }, {
                icon: Leaf,
                title: 'Sustainable Production',
                desc: 'Our manufacturing processes are designed to minimize waste, with strict recycling protocols in our Slovenian facility.'
              }].map((item, i) => <div key={i} className="flex gap-4" data-id="element-345">
                    <div className="mt-1" data-id="element-346">
                      <item.icon className="w-5 h-5 text-robeta-copper" data-id="element-347" />
                    </div>
                    <div data-id="element-348">
                      <h3 className="text-sm font-bold text-robeta-black uppercase tracking-wide mb-2" data-id="element-349">
                        {item.title}
                      </h3>
                      <p className="text-robeta-black/60 font-light text-sm leading-relaxed" data-id="element-350">
                        {item.desc}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. BRAND STATEMENT */}
      <section className="py-32 md:py-48 bg-robeta-black text-white text-center" data-id="element-351">
        <div className="container mx-auto px-6 md:px-12" data-id="element-352">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-353">
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-light uppercase tracking-editorial leading-tight" data-id="element-354">
              "Luxury is not excess.
              <br data-id="element-355" />
              It is{' '}
              <span className="text-robeta-copper font-serif italic" data-id="element-356">
                intention
              </span>
              ."
            </h2>
          </motion.div>
        </div>
      </section>
    </main>;
}